import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../Shared/Button";
import { STATUS } from "../../constants";
import SelectField from "../Shared/SelectField";
import TextInputField from "components/Shared/TextInputField";
import { createSpeciality, updateSpeciality, deleteSpeciality } from "../../services/specialists";
import { fetchSpecialities } from "components/appSlice";
import { StatusCodes } from "http-status-codes";
import CheckboxField from "../Shared/CheckboxField";

const SpecialityEditor = () => {
  const dispatch = useDispatch();
  const specialities = useSelector((state) => state.app.specialities);
  const sites = useSelector((state) => state.app.sites);

  const [selectedSpecialityId, setSelectedSpecialityId] = useState("");
  const [toDeleteSpecialityId, setToDeleteSpecialityId] = useState("");
  const [editedSpecialityName, setEditedSpecialityName] = useState("");
  const [newSpecialityName, setNewSpecialityName] = useState("");
  const [createSubmitStatus, setCreateSubmitStatus] = useState(STATUS.DEFAULT);
  const [updateSubmitStatus, setUpdateSubmitStatus] = useState(STATUS.DEFAULT);
  const [deleteSubmitStatus, setDeleteSubmitStatus] = useState(STATUS.DEFAULT);
  const [createError, setCreateError] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [createSpecialityPublishedTo, setCreateSpecialityPublishedTo] = useState([]);
  const [updateSpecialityPublishedTo, setUpdateSpecialityPublishedTo] = useState([]);

  const specialityOptions = useMemo(
    () =>
      specialities.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    [specialities]
  );

  const handleUpdateSpecialitySelection = (specialityId) => {
    if (specialityId) {
      const selectedSpeciality = specialities.find(({ id }) => specialityId === id);
      setSelectedSpecialityId(specialityId);
      setEditedSpecialityName(selectedSpeciality.name);
      setUpdateSpecialityPublishedTo(selectedSpeciality.publishedTo);
    }
  };

  const handleDeleteSpecialitySelection = (specialityId) => {
    setToDeleteSpecialityId(specialityId);
  };

  const handleCreatingSpeciality = (text) => {
    setNewSpecialityName(text);
  };

  const handleUpdatingSpeciality = (text) => {
    setEditedSpecialityName(text);
  };

  const handleCreateSubmit = async () => {
    setCreateSubmitStatus(STATUS.LOADING);
    if (newSpecialityName) {
      const status = await createSpeciality({ name: newSpecialityName, publishedTo: createSpecialityPublishedTo });

      if (status === StatusCodes.OK) {
        dispatch(fetchSpecialities());
        setNewSpecialityName("");
        setCreateError("");
        setCreateSpecialityPublishedTo([]);
      } else {
        setCreateError("Speciality already exists");
      }
    }
    setCreateSubmitStatus(STATUS.DEFAULT);
  };

  const handleUpdateSubmit = async () => {
    setUpdateSubmitStatus(STATUS.LOADING);
    if (selectedSpecialityId) {
      const status = await updateSpeciality(
        { name: editedSpecialityName, publishedTo: updateSpecialityPublishedTo },
        selectedSpecialityId
      );

      if (status === StatusCodes.OK) {
        dispatch(fetchSpecialities());
        setSelectedSpecialityId("");
        setEditedSpecialityName("");
        setUpdateSpecialityPublishedTo([]);
        setUpdateError("");
      } else {
        setUpdateError("Speciality already exists");
      }
    }
    setUpdateSubmitStatus(STATUS.DEFAULT);
  };

  const handleDeleteSubmit = async () => {
    setDeleteSubmitStatus(STATUS.LOADING);
    if (toDeleteSpecialityId) {
      await deleteSpeciality(toDeleteSpecialityId);
      dispatch(fetchSpecialities());
      setToDeleteSpecialityId("");
      if (toDeleteSpecialityId === selectedSpecialityId) {
        setSelectedSpecialityId("");
        setEditedSpecialityName("");
      }
    }
    setDeleteSubmitStatus(STATUS.DEFAULT);
  };

  const isPublishedToSite = (site, publishedTo) => {
    return publishedTo.filter((siteBeenPublishedTo) => siteBeenPublishedTo.id === site.id).length > 0;
  };

  const publishedToSection = (publishedTo, setPublishedTo) => {
    return (
      <>
        <label className="label is-small">{"Published To"}</label>
        <div className="columns">
          {sites.map((site) => {
            return (
              <div className="column" key={site.id}>
                <CheckboxField
                  label={site.siteName}
                  checked={isPublishedToSite(site, publishedTo)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPublishedTo([...publishedTo, site]);
                    } else {
                      setPublishedTo(publishedTo.filter((siteBeenPublishedTo) => siteBeenPublishedTo.id !== site.id));
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="columns">
      <div className="column is-one-second">
        <div className="column is-full">
          <p className="menu-label">Create Speciality</p>
          <TextInputField
            label="Enter new speciality "
            value={newSpecialityName}
            isRequired
            onChange={(e) => handleCreatingSpeciality(e.target.value)}
          />
          {publishedToSection(createSpecialityPublishedTo, setCreateSpecialityPublishedTo)}
          {createError && <div className="notification is-danger is-size-7 is-radiusless ">{createError}</div>}
          <Button text="Create" isLoading={createSubmitStatus === STATUS.LOADING} onClick={handleCreateSubmit} />
          <hr />
        </div>
        <div className="column is-full">
          <p className="menu-label">Update Speciality</p>
          <SelectField
            label="Select speciality"
            options={specialityOptions}
            value={selectedSpecialityId}
            isRequired
            onChange={(e) => handleUpdateSpecialitySelection(e.target.value)}
          />
          <TextInputField
            label="Edit speciality name"
            value={editedSpecialityName}
            onChange={(e) => handleUpdatingSpeciality(e.target.value)}
          />
          {publishedToSection(updateSpecialityPublishedTo, setUpdateSpecialityPublishedTo)}
          {updateError && <div className="notification is-danger is-size-7 is-radiusless ">{updateError}</div>}
          <Button text="Update" isLoading={updateSubmitStatus === STATUS.LOADING} onClick={handleUpdateSubmit} />
          <hr />
        </div>
        <div className="column is-full">
          <p className="menu-label">Delete Speciality</p>
          <SelectField
            label="Select speciality"
            options={specialityOptions}
            value={toDeleteSpecialityId}
            isRequired
            onChange={(e) => handleDeleteSpecialitySelection(e.target.value)}
          />
          <Button text="Delete" isLoading={deleteSubmitStatus === STATUS.LOADING} onClick={handleDeleteSubmit} />
        </div>
      </div>
      <div className="column" />
    </div>
  );
};

export default SpecialityEditor;
